import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';

import {Subscription} from 'rxjs';
import {WpService} from '../../../../services/wp.service';
import {LoaderService} from '../../../../services/loader.service';
import {Breadcrumb} from '../../../../types/Breadcrumb';
import {RoutesEnums, RoutesNamesEnum} from '../../../../enums/RoutesEnums';
import {ToolsService} from '../../../../services/tools.service';
import {MediaSizeEnum} from '../../../../enums/WPEnums';
import {MetaTag} from '../../../../types/MetaTag';
import {SeoMetaService} from '../../../../services/seo-meta.service';
import {UrlBuilderService} from '../../../../services/url-builder.service';
import {WPPostNative} from '../../../../types/WPPostNative';
import * as dayjs from 'dayjs';
import 'dayjs/locale/sv';
import {GaPreffixEnum} from '../../../../enums/AdEnums';
import {defaultIfEmpty, filter, finalize, skipWhile, tap} from 'rxjs/operators';
import {NativesPostUrlResolverService} from '../../services/natives-post-url-resolver.service';
import {LoaderQueueItem} from '../../../../types/LoaderQueueItem';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';
import {DataLayerParamsPageViewGA4} from '../../../../types/DataLayerParamsPageViewGA4';
import {PageViewsGa4ResolverService} from '../../../../services/page-views-ga4-resolver.service';
import {DataLayerParamsClickGA4} from '../../../../types/DataLayerParamsClickGA4';
import {AdcampaignService} from '../../../../services/adcampaign.service';
import {QueryParams} from '../../../../types/QueryParams';
import {AppConfigService} from '../../../../services/app-config.service';
import {SearchService} from '../../../../services/search/search.service';
import {SearchReloadService} from '../../../../services/search/search-reload.service';
import {isPlatformBrowser} from '@angular/common';
import {ScriptNameEnum} from '../../../../enums/SsciptsEnums';
import {CodeSnippetsService} from '../../../../services/code-snippets.service';
import {StaticNativesService} from '../../../../services/static-natives.service';
import {StaticSidebarPuffsService} from '../../../../services/static-sidebar-puffs.service';

declare var hbspt: any;

@Component({
  selector: 'app-native-post',
  templateUrl: './native-post.component.html',
  styleUrls: ['./native-post.component.scss', '../../../../../styles/misc/wp-block-library.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NativePostComponent implements OnInit, OnDestroy {
  public breadcrumbs: Breadcrumb[] = [];
  public post: WPPostNative;
  public publishDate: string;
  public mediaSizeEnum = MediaSizeEnum;
  public appStoreImageUrl: string = '';
  public googlePlayImageUrl: string = '';
  public adName: string;
  public customGa4Params: DataLayerParamsClickGA4;
  public pageType: DLPageTypeEnum = DLPageTypeEnum.ARTICLE_PAGE;
  private pageTitle: string;
  private subscriptions: Subscription[] = [];

  private codeSnippets = inject(CodeSnippetsService);
  private loadedScripts = this.codeSnippets.loadedScripts;

  constructor(
    private wpService: WpService,
    private nativesService: StaticNativesService,
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private router: Router,
    private tools: ToolsService,
    private metaService: SeoMetaService,
    private urlBuilder: UrlBuilderService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private postUrlResolver: NativesPostUrlResolverService,
    private adCampaignService: AdcampaignService,
    private appConfigService: AppConfigService,
    private sidebarPuffsService: StaticSidebarPuffsService,
    private searchService: SearchService,
    private _change: ChangeDetectorRef,
    private reloadService: SearchReloadService,
    private _renderer2: Renderer2,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    dayjs.locale('sv');

    effect(() => {
      if (this.loadedScripts()[ScriptNameEnum.HUBSPOT_FORMS_EMBED]) {
        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            this.ngZone.run(() => {
              this.loadTrottForm();
            });
          }, 1000);
        });
      }
    });
  }

  ngOnInit() {
    this.searchService.softResetState();
    this.searchService.resetWidgetState();
    this.onRouteUpdate();
    this.appStoreImageUrl = this.appConfigService.appConfig.amazonS3Url + 'assets/app-store.webp';
    this.googlePlayImageUrl = this.appConfigService.appConfig.amazonS3Url + 'assets/google-play.webp';

    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.onRouteUpdate();
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.wpService.removeSchema();
  }

  public generateDataLayerParamsClickGA4(callToAction: string): DataLayerParamsClickGA4 {
    const params: DataLayerParamsClickGA4 = this.customGa4Params
      ? this.customGa4Params
      : {
          callToAction: '',
          pageType: this.pageType,
        };
    params.callToAction = callToAction;
    return params;
  }

  private onRouteUpdate(): void {
    this.reloadService.updateParams(this.route.snapshot.params, this.route.snapshot.queryParams);
    const isRedirected: boolean = this.isRedirectedAfterInitUrlResolverAndUpdateState();
    this.loadPostData(isRedirected);
  }

  private isRedirectedAfterInitUrlResolverAndUpdateState(): boolean {
    const isRedirected: boolean = this.postUrlResolver.cleanUpUrl(this.route.snapshot);
    this.postUrlResolver.resetState();
    this.postUrlResolver.processRouteAndQueryParams(this.route.snapshot.params, this.route.snapshot.queryParams);
    const isRedirectedFromOldUrl: boolean = this.postUrlResolver.conditionalRedirectBrokenUrlsFromOldWebsite();
    return isRedirected || isRedirectedFromOldUrl;
  }

  private loadPostData(isRedirected: boolean): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);

    this.subscriptions.push(
      this.nativesService
        .GetNativePostById(this.postUrlResolver.postId)
        .pipe(
          filter((result) => this.filterPost(result)),
          defaultIfEmpty(null),
          tap((result) => this.conditionalRedirectToArticlesList(result)),
          skipWhile((result) => result === null),
          finalize(() => {
            this.loaderService.removeFromQueue(queryId);
          }),
        )
        .subscribe(
          (result) => this.processPostResults(result, isRedirected),
          (error) => {
            this.postUrlResolver.processFailToGetPostData(
              error,
              `native post get: ${this.postUrlResolver.postId} ${this.postUrlResolver.isPreview}`,
            );
            this.onDataLoadingFinish();
          },
        ),
    );
  }

  private conditionalRedirectToArticlesList(result: WPPostNative): void {
    if (result === null) {
      this.postUrlResolver.processFailToGetPostData('No items to show', 'Preview item filtered out');
    }
  }

  private filterPost(result: WPPostNative): boolean {
    return (
      this.postUrlResolver.isPreview ||
      (result && !result.acf) ||
      (result && result.acf && !result.acf.isPreview) ||
      (result && result.acf && result.acf.isPreview === false)
    );
  }

  private processPostResults(results: WPPostNative, isRedirected: boolean): void {
    const wasRedirectedInvalidSlug: boolean = this.postUrlResolver.conditionalRedirectToValidUrl(results);
    if (this.tools.isNil(results)) {
      this.postUrlResolver.processFailToGetPostData(
        'Empty post data',
        `native post get: ${this.postUrlResolver.postId} ${this.postUrlResolver.isPreview}`,
      );
      this.onDataLoadingFinish();
    } else {
      this.post = results;
      this.updatePostUrls();
      this.buildBreadcrumbs();
      this.updateMetaTags();
      this.scrollToPostTop();
      this.setBasicPostData();
      this.wpService.removeSchema();
      this.wpService.addSchema(null, null, null, null, this.post);
      this.sidebarPuffsService.reloadCombinedPuffs(
        RoutesEnums.NATIVES,
        null,
        this.post.care_provider_unit_type,
        null,
        null,
        [this.post.id],
      );
      if (isRedirected == false && wasRedirectedInvalidSlug === false) {
        // this.loadAdCampaignOwnersAndTriggerDataLayerTracking(); ignore assigned taxonomies and ask for campaign ID
        this.loadAdCampaignOwnerAndTriggerDataLayerTracking();
        this.conditionalLoadHubspotFormEmbed();
      }
      this.onDataLoadingFinish();
    }
  }

  private loadAdCampaignOwnersAndTriggerDataLayerTracking(): void {
    this.subscriptions.push(
      this.adCampaignService
        .getAdCampaignOwners(this.post.acf.campaignCareServiceType, this.post.acf.campaignSymptom)
        .subscribe(
          (result) => {
            this.triggerDataLayerTracking(result && result.length > 0 ? result[0] : null);
          },
          (error) => {
            this.triggerDataLayerTracking(null);
          },
        ),
    );
  }

  private loadAdCampaignOwnerAndTriggerDataLayerTracking(): void {
    if (!this.tools.isNil(this.post.acf.adCampaignId) && this.post.acf.adCampaignId.toString() !== '') {
      this.subscriptions.push(
        this.adCampaignService.getAdCampaignOwner(this.post.acf.adCampaignId).subscribe(
          (result) => {
            this.triggerDataLayerTracking(result);
          },
          (error) => {
            this.triggerDataLayerTracking(null);
          },
        ),
      );
    } else {
      this.triggerDataLayerTracking(null);
    }
  }

  private triggerDataLayerTracking(adCampaignOwner: number): void {
    const customGa4Params: DataLayerParamsPageViewGA4 = this.wpService.createDataLayerGa4CustomParams(
      this.post,
      DLPageTypeEnum.ARTICLE_PAGE,
      this.pageTitle,
    );
    customGa4Params.adCampaignOwner = adCampaignOwner;
    this.pageViewGa4Resolver.trackPageView(customGa4Params);

    this.customGa4Params = this.wpService.createDataLayerGa4ClickCustomParams(
      this.post,
      '',
      DLPageTypeEnum.ARTICLE_PAGE,
      this.pageTitle,
    );
  }

  private setBasicPostData(): void {
    this.publishDate = `${dayjs(this.post.date).format('D MMMM YYYY')} kl ${dayjs(this.post.date).format('HH:mm')}`;
    this.adName = `${GaPreffixEnum.NATIVE_ARTICLE} ${this.post.acf.trackingTitle}`;
  }

  private buildBreadcrumbs() {
    this.breadcrumbs = [];
    this.breadcrumbs.push({
      routeParams: ['/', RoutesEnums.NATIVES],
      routeQueryParams: null,
      anchor: RoutesNamesEnum.NATIVES,
    });
    this.breadcrumbs.push({
      routeParams: null,
      routeQueryParams: null,
      anchor: this.post.title.rendered,
    });
  }

  private scrollToPostTop(): void {
    this.tools.scrollTo();
  }

  private updateMetaTags(): void {
    const metaTags: MetaTag[] = [];
    const suffix = ' - Vården.se';
    const url = this.urlBuilder.CreateStaticLink(
      this.urlBuilder.CreateNativePostRouterLink(this.post.id, this.post.slug),
    );
    const title = this.post.acf != null ? this.post.acf.seoTitle : '';
    this.pageTitle = title + suffix;

    metaTags.push({
      value: title + suffix,
      type: 'title',
      field: 'title',
    });

    metaTags.push({
      value: title + suffix,
      type: 'property',
      field: 'og:title',
    });

    metaTags.push({
      value: 'website',
      type: 'property',
      field: 'og:type',
    });

    metaTags.push({
      value: url,
      type: 'property',
      field: 'og:url',
    });

    metaTags.push({
      value: this.post.acf != null ? this.post.acf.seoDescription : '',
      type: 'name',
      field: 'description',
    });

    metaTags.push({
      value: this.post.acf != null ? this.post.acf.seoDescription : '',
      type: 'property',
      field: 'og:description',
    });

    metaTags.push({
      value: this.appConfigService.appConfig.siteName,
      type: 'property',
      field: 'og:site_name',
    });

    if (!this.tools.isNil(this.post.acf.mainImage) && this.post.acf.mainImage.id > 0) {
      metaTags.push({
        value: this.wpService.getImageFlatUrl(this.post.acf.mainImage),
        type: 'property',
        field: 'og:image',
      });

      metaTags.push({
        value: this.wpService.getImageFlatUrl(this.post.acf.mainImage),
        type: 'name',
        field: 'twitter:image',
      });

      metaTags.push({
        value: 'summary_large_image',
        type: 'name',
        field: 'twitter:card',
      });
    }

    this.metaService.updateMetaTags(metaTags);
  }

  private onDataLoadingFinish(): void {
    this._change.markForCheck();
  }

  private updatePostUrls() {
    this.post.acf.logoUrlRouterLink = null;
    this.post.acf.logoUrlQueryParams = null;
    this.post.acf.moreBtnUrlRouterLink = null;
    this.post.acf.moreBtnUrlQueryParams = null;

    if (this.post.acf.showLogo) {
      const convertedUrl: [string[], QueryParams] = this.urlBuilder.convertUrlToRouterLink(this.post.acf.logoUrl);
      this.post.acf.logoUrlRouterLink = convertedUrl ? convertedUrl[0] : null;
      this.post.acf.logoUrlQueryParams = convertedUrl ? convertedUrl[1] : null;
    }

    if (this.post.acf.showMoreButton) {
      const convertedUrl: [string[], QueryParams] = this.urlBuilder.convertUrlToRouterLink(this.post.acf.moreBtnUrl);
      this.post.acf.moreBtnUrlRouterLink = convertedUrl ? convertedUrl[0] : null;
      this.post.acf.moreBtnUrlQueryParams = convertedUrl ? convertedUrl[1] : null;
    }
  }

  private conditionalLoadHubspotFormEmbed(): void {
    if (isPlatformBrowser(this.platformId) && this.post.content.rendered.includes('id="trottForm"')) {
      const hsScript: string = '//js.hsforms.net/forms/embed/v2.js';
      this.codeSnippets.loadExternalScript(this._renderer2, hsScript, ScriptNameEnum.HUBSPOT_FORMS_EMBED);
    }
  }

  private loadTrottForm() {
    hbspt.forms.create({
      region: 'na1',
      portalId: '7080361',
      formId: '910aaace-c0ad-41bc-b297-c06f36a40f0e',
      target: '#trottForm',
    });
  }
}
